import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { apiBaseUrl, apiAuthBaseUrl, workerBaseUrl } from '../../../config';
import ListTable from '../../../Components/ListTable';
import SearchInput from '../../../Components/SearchInput';
import useTranslation from '../../../Hooks/useTranslation';
import { useAuth } from '../../../Contexts/AuthProvider';
import { useAppSettings } from '../../../Contexts/AppSettingsProvider';
import { Switch, Checkbox, TextField, IconButton, Badge, Popover, Button, CircularProgress, MenuItem, Select } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HistoryIcon from '@material-ui/icons/History';
import moment from 'moment';
import AlertToastr from 'utils/alert';
import TablePagination from '../../PredictiveMaintenance/Equipment/TablePagination';
import SettingsDateRangePicker from '../../../Components/SettingsDateRangePicker';
import DataUsageStatsChart from '../../../Components/Charts/DataUsageStatsChart';
import UnitSettingsLog from './UnitSettingsLog';
import FleetViewSettings from './FleetViewSettings';
import CompanySlidePanel from '../../../Components/Company/CompanySlidePanel';
import JobsCacheBuildLogDialog from './JobsCacheBuildLogDialog';

const FILTER_KEYS = ['month', 'unit', 'connection', 'license', 'jobType'];

const DATAUSAGE_SETTINGS_STORAGE_KEY = 'dataUsage.settings';
const AUTO_REFRESH_OPTIONS = [
    { value: 'off', label: 'Off' },
    { value: '15s', label: '15s', ms: 15000 },
    { value: '30s', label: '30s', ms: 30000 },
    { value: '1m', label: '1m', ms: 60000 },
    { value: '5m', label: '5m', ms: 300000 },
    { value: '15m', label: '15m', ms: 900000 },
];

/** One parse on mount — period, custom range bounds, auto-refresh. */
function getInitialDataUsageSettingsFromStorage() {
    const defaults = {
        period: 9,
        dateFrom: null,
        dateTo: null,
        customRangeName: null,
        autoRefreshInterval: 'off',
        autoRefreshStartTime: null,
    };
    try {
        const raw = localStorage.getItem(DATAUSAGE_SETTINGS_STORAGE_KEY);
        if (!raw) return defaults;
        const parsed = JSON.parse(raw);
        const next = { ...defaults };
        if (parsed.period !== undefined && parsed.period !== null) {
            if (parsed.period === 'customRange') {
                if (typeof parsed.dateFrom === 'string' && typeof parsed.dateTo === 'string') {
                    next.period = 'customRange';
                    next.dateFrom = parsed.dateFrom;
                    next.dateTo = parsed.dateTo;
                    if (typeof parsed.customRangeName === 'string') next.customRangeName = parsed.customRangeName;
                }
            } else {
                next.period = parsed.period;
            }
        }
        const v = parsed.autoRefreshInterval;
        if (AUTO_REFRESH_OPTIONS.some((o) => o.value === v)) next.autoRefreshInterval = v;
        const t = parsed.autoRefreshStartTime;
        if (typeof t === 'number' && !Number.isNaN(t)) next.autoRefreshStartTime = t;
        return next;
    } catch (_) {
        return defaults;
    }
}

const DETAILED_COLUMNS = [
    { key: 'month', label: 'Month' },
    { key: 'unit', label: 'Unit' },
    { key: 'connection', label: 'Connection' },
    { key: 'license', label: 'License' },
    { key: 'jobType', label: 'Job Type' },
];

const DEFAULT_PAGE_SIZE = 10;

const DetailedTableContent = React.memo(({
    detailedList,
    totalCount = 0,
    page = 0,
    pageSize = DEFAULT_PAGE_SIZE,
    onPageChange,
    onPageSizeChange,
    detailedFilters,
    setDetailedFilter,
    getDisplayValue,
    serverFilterOptions = null,
    totalJobs: serverTotalJobs = null,
    totalSizeMb: serverTotalSizeMb = null,
    totalSizeGb: serverTotalSizeGb = null,
    serverTotalPages = null,
    serverPage = null,
    serverPageSize = null,
    isFetching = false,
}) => {
    const [filterAnchor, setFilterAnchor] = useState({ key: null, el: null });
    const [popoverSearchTerm, setPopoverSearchTerm] = useState('');
    const [pendingFilterValue, setPendingFilterValue] = useState([]);
    const filterAnchorElRef = useRef(null);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
    const detailedFiltersRef = useRef(detailedFilters);
    detailedFiltersRef.current = detailedFilters;

    useEffect(() => {
        if (filterAnchor.key != null) {
            setPendingFilterValue(detailedFiltersRef.current[filterAnchor.key] || []);
        }
    }, [filterAnchor.key]);

    // Filter options from backend (response.filters); no client-side filtering — backend returns already filtered items
    const detailedFilterOptions = useMemo(() => {
        if (!serverFilterOptions || typeof serverFilterOptions !== 'object') {
            return Object.fromEntries(FILTER_KEYS.map((key) => [key, []]));
        }
        const opts = {};
        FILTER_KEYS.forEach((key) => {
            const arr = serverFilterOptions[key] || [];
            opts[key] =
                key === 'month'
                    ? [...arr].sort((a, b) => String(b).localeCompare(String(a)))
                    : [...arr].sort((a, b) => String(a).localeCompare(String(b)));
        });
        return opts;
    }, [serverFilterOptions]);

    /** Backend sends raw ids in filters.units / filters.licenses; rows use full `unit` / `license` strings — match for display and search. */
    const getDetailedFilterOptionLabel = useCallback((filterKey, opt) => {
        const raw = String(opt);
        if (filterKey === 'unit') {
            const row = detailedList.find(
                (item) => String(item.unitUId) === raw || item.unitUId === opt
                    || (typeof item.unit === 'string' && item.unit.endsWith(raw)),
            );
            if (row?.unit) return row.unit;
            return raw;
        }
        if (filterKey === 'license') {
            const row = detailedList.find((item) => String(item.licenseId) === raw);
            if (row?.license) return row.license;
            return raw;
        }
        return raw;
    }, [detailedList]);

    const handleFilterIconClick = useCallback((key, event) => {
        setFilterAnchor((prev) => (prev.key === key ? { key: null, el: null } : { key, el: event.currentTarget.closest('th') || event.currentTarget }));
        setPopoverSearchTerm('');
    }, []);
    const handleCloseFilterPopper = useCallback(() => {
        setFilterAnchor({ key: null, el: null });
        setPopoverAnchorEl(null);
        setPopoverSearchTerm('');
    }, []);

    useLayoutEffect(() => {
        if (filterAnchor.key) {
            setPopoverAnchorEl(filterAnchorElRef.current);
        } else {
            setPopoverAnchorEl(null);
        }
    }, [filterAnchor.key, detailedList]);

    const lastColStyle = { padding: '8px 12px', width: '100px', maxWidth: '100px', boxSizing: 'border-box', overflow: 'hidden' };
    const hasServerTotals = typeof serverTotalJobs === 'number' || typeof serverTotalSizeMb === 'number' || typeof serverTotalSizeGb === 'number';
    const totalJobCount = hasServerTotals && typeof serverTotalJobs === 'number'
        ? serverTotalJobs
        : detailedList.reduce((sum, item) => sum + (Number(item.jobCount) || 0), 0);
    const totalSizeGb = hasServerTotals && typeof serverTotalSizeGb === 'number'
        ? serverTotalSizeGb
        : detailedList.reduce((sum, item) => {
            const gb = Number(item.totalSizeGb);
            if (!Number.isNaN(gb)) return sum + gb;
            const mb = Number(item.totalSizeMb);
            if (!Number.isNaN(mb)) return sum + mb / 1024;
            const kb = Number(item.totalSizeKb);
            if (!Number.isNaN(kb)) return sum + kb / 1024 / 1024;
            return sum;
        }, 0);
    const totalSizeMb = hasServerTotals && typeof serverTotalSizeMb === 'number'
        ? serverTotalSizeMb
        : detailedList.reduce((sum, item) => {
            const mb = Number(item.totalSizeMb);
            if (!Number.isNaN(mb)) return sum + mb;
            const kb = Number(item.totalSizeKb);
            if (!Number.isNaN(kb)) return sum + kb / 1024;
            return sum;
        }, 0);

    if (detailedList.length === 0 && !hasServerTotals) {
        return <div style={{ padding: 16, color: '#757575', textAlign: 'center' }}>No detailed data.</div>;
    }

    const dataKey = `${detailedList.length}-${detailedList[0]?.month ?? ''}-${page}`;

    return (
        <div className="dataUsageDetailedTable" style={{ padding: '6px 16px', backgroundColor: 'rgba(0,0,0,0.02)', position: 'relative' }}>
            {isFetching && (
                <div
                    className="dataUsageDetailedTableOverlay"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        transition: 'opacity 0.2s ease',
                    }}
                >
                    <CircularProgress size={36} style={{ color: 'rgba(0, 0, 0, 0.4)' }} />
                </div>
            )}
            <div key={dataKey} className="dataUsageDetailedTableInner">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
                        {DETAILED_COLUMNS.map(({ key, label }) => {
                            const selectedCount = (detailedFilters[key] || []).length;
                            const uniqueOptionsCount = (detailedFilterOptions[key] || []).length;
                            const isOpenColumn = filterAnchor.key === key;
                            return (
                                <th
                                    key={key}
                                    ref={isOpenColumn ? (el) => { filterAnchorElRef.current = el; } : undefined}
                                    style={{ padding: '8px 12px', verticalAlign: 'middle' }}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: "25px" }}>
                                        {label}
                                        {key !== 'month' && (
                                            <>
                                                {' : '}
                                                {uniqueOptionsCount.toLocaleString()}
                                            </>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleFilterIconClick(key, e)}
                                            style={{ padding: 4 }}
                                            aria-label={`Filter ${label}`}
                                        >
                                            <Badge badgeContent={selectedCount > 0 ? selectedCount : null} color="primary" overlap="rectangular">
                                                <FilterListIcon fontSize="medium" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                            </Badge>
                                        </IconButton>
                                    </span>
                                </th>
                            );
                        })}
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>
                            <div style={{ textAlign: 'left', display: 'flex', gap: "5px" }}>
                                <div>Job Count:</div>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>{totalJobCount.toLocaleString()}</div>
                            </div>
                        </th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>
                            <div style={{ textAlign: 'left', display: 'flex', gap: "5px" }}>
                                <div>Total Size (MB):</div>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>{totalSizeMb.toFixed(2)}</div>
                            </div>
                        </th>
                        <th style={{ ...lastColStyle, textAlign: 'left' }}>
                            <div style={{ textAlign: 'left', display: 'flex', gap: "5px" }}>
                                <div>Total Size (GB):</div>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>{totalSizeGb.toFixed(2)}</div>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {detailedList.length === 0 ? (
                        <tr>
                            <td colSpan={8} style={{ padding: 16, color: '#757575', textAlign: 'center' }}>
                                No data
                            </td>
                        </tr>
                    ) : (
                        detailedList.map((item, idx) => (
                            <tr key={(item.unitUId || '') + (item.licenseId || '') + idx} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '8px 12px' }}>{item.month ?? '-'}</td>
                                <td style={{ padding: '8px 12px' }}>{item.unit ?? item.unitName ?? item.unitUId ?? '-'}</td>
                                <td style={{ padding: '8px 12px' }}>{item.connection ?? '-'}</td>
                                <td style={{ padding: '8px 12px' }}>{item.license ?? item.licenseName ?? item.licenseId ?? '-'}</td>
                                <td style={{ padding: '8px 12px' }}>{item.jobType ?? '-'}</td>
                                <td style={{ padding: '8px 12px' }}>{item.jobCount ?? '-'}</td>
                                <td style={{ padding: '6px 12px' }}>{item.totalSizeMb ?? '-'}</td>
                                <td style={{ ...lastColStyle, padding: '6px 12px' }}>{item.totalSizeGb ?? '-'}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            {totalCount > 0 && (
                <div style={{ padding: '8px 0' }}>
                    <TablePagination
                        pagination={{
                            pageSize: serverPageSize ?? pageSize,
                            pageNumber: serverPage != null ? serverPage : page + 1,
                            itemsCount: detailedList.length,
                            itemsTotalCount: totalCount,
                            pageCount: serverTotalPages ?? (Math.ceil(totalCount / pageSize) || 0),
                        }}
                        onPrevPage={() => onPageChange && onPageChange(page - 1)}
                        onNextPage={() => onPageChange && onPageChange(page + 1)}
                        handleRowCount={(newPageSize) => onPageSizeChange && onPageSizeChange(newPageSize)}
                    />
                </div>
            )}
            <Popover
                open={!!filterAnchor.key && !!popoverAnchorEl}
                anchorEl={popoverAnchorEl}
                onClose={handleCloseFilterPopper}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ style: { marginTop: 4, padding: 8, minWidth: 200, maxWidth: 320 } }}
            >
                {filterAnchor.key && (() => {
                    const key = filterAnchor.key;
                    const allOptions = detailedFilterOptions[key] || [];
                    const search = popoverSearchTerm.trim().toLowerCase();
                    const options = search
                        ? allOptions.filter((opt) => {
                            const label = getDetailedFilterOptionLabel(key, opt);
                            const hay = `${String(opt)} ${label}`.toLowerCase();
                            return hay.includes(search);
                        })
                        : allOptions;
                    const toggleOption = (opt) => {
                        const next = pendingFilterValue.includes(opt)
                            ? pendingFilterValue.filter((o) => o !== opt)
                            : [...pendingFilterValue, opt];
                        setPendingFilterValue(next);
                    };
                    const handleApply = () => {
                        setDetailedFilter(key, pendingFilterValue);
                        handleCloseFilterPopper();
                    };
                    const handleClear = () => {
                        setPendingFilterValue([]);
                        setDetailedFilter(key, []);
                        handleCloseFilterPopper();
                    };
                    return (
                        <div style={{ minWidth: 200 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={handleClear}
                                    disabled={pendingFilterValue.length === 0}
                                    style={{ textTransform: 'none' }}
                                >
                                    Clear filter
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={handleApply}
                                    disabled={pendingFilterValue.length === 0}
                                    style={{ textTransform: 'none' }}
                                >
                                    Apply
                                </Button>
                            </div>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search..."
                                value={popoverSearchTerm}
                                onChange={(e) => setPopoverSearchTerm(e.target.value)}
                                fullWidth
                                style={{ marginBottom: 8 }}
                                inputProps={{ style: { fontSize: 13 } }}
                            />
                            <div
                                className="dataUsageDetailedFilterListbox"
                                style={{ maxHeight: 280, overflowY: 'auto', paddingLeft: 0 }}
                            >
                                {options.map((opt) => (
                                    <div
                                        key={String(opt)}
                                        onClick={() => toggleOption(opt)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '6px 0',
                                            cursor: 'pointer',
                                            fontSize: 13,
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleOption(opt);
                                            }
                                        }}
                                    >
                                        <Checkbox
                                            color="primary"
                                            size="small"
                                            style={{ marginRight: 8, padding: 4 }}
                                            checked={pendingFilterValue.includes(opt)}
                                            onClick={(e) => { e.stopPropagation(); toggleOption(opt); }}
                                        />
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {getDetailedFilterOptionLabel(key, opt)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}
            </Popover>
            </div>
        </div>
    );
});

const DataUsage = () => {
    const { appTheme } = useAppSettings();
    const isDarkTheme = appTheme === 'Dark';
    const settingsSelectArrowColor = isDarkTheme ? 'rgba(255,255,255,0.54)' : 'rgba(0,0,0,0.54)';
    const { userLoaded, currentUser, currentCompany } = useAuth();
    const translation = useTranslation();
    const queryClient = useQueryClient();
    const [data, setData] = useState([]);
    const [activeTab, setActiveTab] = useState('Companies');
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companyPanelOpen, setCompanyPanelOpen] = useState(false);
    const [selectedPanelCompanyId, setSelectedPanelCompanyId] = useState(null);
    const [cacheLogCompany, setCacheLogCompany] = useState(null);

    const showDataUsageTab = currentUser?.allowSystemUsage?.() ?? false;
    const showSettingsTab = currentUser?.allowSystemSettings?.() ?? false;
    // Fleet View Settings should be visible only for SuperUser/Master and only when enabled in `me`.
    const showFleetViewSettingsTab = Boolean(
        currentUser?.allowUiFleetView?.()
        && (currentUser?.isSuperuser?.() || currentUser?.isMaster?.())
    );

    useEffect(() => {
        if (!userLoaded || !currentUser) return;
        if (activeTab === 'Companies' && !showDataUsageTab && showSettingsTab) {
            setActiveTab('Settings');
        } else if (activeTab === 'Settings' && !showSettingsTab && showDataUsageTab) {
            setActiveTab('Companies');
        } else if (activeTab === 'Fleet View Settings' && !showFleetViewSettingsTab) {
            if (showDataUsageTab) setActiveTab('Companies');
            else if (showSettingsTab) setActiveTab('Settings');
            else setActiveTab('Unit Settings Log');
        }
    }, [userLoaded, currentUser, showDataUsageTab, showSettingsTab, showFleetViewSettingsTab, activeTab]);
    const [expandedCompanyIds, setExpandedCompanyIds] = useState([]);
    const [detailedFiltersByCompany, setDetailedFiltersByCompany] = useState({});
    const [detailedPaginationByCompany, setDetailedPaginationByCompany] = useState({});
    const [settingsSearchTerm, setSettingsSearchTerm] = useState('');
    const initialDataUsageSettingsRef = useRef(null);
    if (initialDataUsageSettingsRef.current === null) {
        initialDataUsageSettingsRef.current = getInitialDataUsageSettingsFromStorage();
    }
    const initialDataUsageSettings = initialDataUsageSettingsRef.current;
    const [settingsDateType, setSettingsDateType] = useState(initialDataUsageSettings.period);
    const [settingsPeriodPopoverAnchor, setSettingsPeriodPopoverAnchor] = useState(null);
    const [settingsAutoRefreshInterval, setSettingsAutoRefreshInterval] = useState(initialDataUsageSettings.autoRefreshInterval);
    const [settingsAutoRefreshStartTime, setSettingsAutoRefreshStartTime] = useState(initialDataUsageSettings.autoRefreshStartTime);
    const [settingsAutoRefreshSelectAnchor, setSettingsAutoRefreshSelectAnchor] = useState(null);
    const [settingsDateFrom, setSettingsDateFrom] = useState(initialDataUsageSettings.dateFrom);
    const [settingsDateTo, setSettingsDateTo] = useState(initialDataUsageSettings.dateTo);
    const [settingsCustomRangeName, setSettingsCustomRangeName] = useState(initialDataUsageSettings.customRangeName);
    const [settingsTableSortBy, setSettingsTableSortBy] = useState(null);
    const [settingsTableSortOrder, setSettingsTableSortOrder] = useState('asc');
    const [chartAverageRequestSizeUnit, setChartAverageRequestSizeUnit] = useState(0);
    const queueToggleInProgressRef = useRef(false);
    const togglingCompanyIdRef = useRef(null);

    const handleCompanyNameClick = useCallback((e, companyId) => {
        e.stopPropagation();
        setSelectedPanelCompanyId(companyId);
        setCompanyPanelOpen(true);
    }, []);

    const tableColumns = useMemo(() => {
        return [
            {
                Header: translation.dataUsageCompanyNameLabel,
                accessor: 'companyName',
                Cell: ({ row, value }) => {
                    const companyId = row.original.companyId ?? row.original.id;
                    return (
                        <button
                            type="button"
                            className="companyNameLink"
                            onClick={(e) => handleCompanyNameClick(e, companyId)}
                        >
                            {value}
                        </button>
                    );
                },
            },
            {
                Header: translation.dataUsageTotalImportsLabel,
                accessor: 'totalImports'
            },
            {
                Header: translation.dataUsageTotalJobsLabel,
                accessor: 'totalJobs'
            },
            {
                Header: translation.dataUsageTotalUnitsLabel,
                accessor: 'totalUnits'
            },
            {
                Header: translation.dataUsageTotalLocationsLabel,
                accessor: 'totalLocations'
            },
            {
                Header: translation.dataUsageUniqueUnitsLabel,
                accessor: 'uniqueUnits'
            },
            {
                Header: translation.dataUsageTotalSizeLabel,
                accessor: 'totalSizeMb'
            },
            {
                Header: translation.dataUsageTotalSizeGbLabel,
                accessor: 'totalSizeGb'
            },
        ];
    }, [translation, handleCompanyNameClick]);

    const listRequest = useQuery(
        ['data-usage', currentCompany],
        async () => {
            const config = !currentUser.isSuperuser() ? {} : {
                headers: {
                    'CompanyId': currentCompany?.id,
                },
            };
            return await axios.get(`${apiBaseUrl}/system/usage`, config);
        },
        {
            keepPreviousData: true,
            enabled: userLoaded && showDataUsageTab && activeTab === 'Companies',
            refetchOnWindowFocus: false,
        },
    );

    const defaultDetailedFilters = useMemo(
        () => ({ month: [], unit: [], connection: [], license: [], jobType: [], jobCount: [], totalSize: [] }),
        [],
    );

    const getDefaultPagination = useCallback(() => ({ page: 0, pageSize: DEFAULT_PAGE_SIZE }), []);

    const detailedQueries = useQueries(
        expandedCompanyIds.map((companyId) => {
            const filters = detailedFiltersByCompany[companyId] || defaultDetailedFilters;
            const pagination = detailedPaginationByCompany[companyId] || getDefaultPagination();
            const body = {
                companyId,
                months: filters.month || [],
                units: filters.unit || [],
                connections: filters.connection || [],
                licenses: filters.license || [],
                jobTypes: (filters.jobType || []).map((v) => (typeof v === 'number' ? v : Number(v))).filter((n) => !Number.isNaN(n)),
                page: pagination.page,
                pageSize: pagination.pageSize,
            };
            return {
                queryKey: ['data-usage-filter', companyId, JSON.stringify(body)],
                queryFn: async () => {
                    const headers = {
                        'Content-Type': 'application/json',
                        ...(currentUser.isSuperuser() && currentCompany?.id ? { CompanyId: currentCompany.id } : {}),
                    };
                    const res = await axios.post(
                        `${apiBaseUrl}/System/usage/filter`,
                        body,
                        { headers },
                    );
                    const data = res.data || {};
                    const items = Array.isArray(data.items) ? data.items : (Array.isArray(data.data) ? data.data : []);
                    const totalCount = typeof data.totalCount === 'number' ? data.totalCount : (data.totalCount ?? items.length);
                    const filtersFromApi = data.filters || {};
                    const totalPages = typeof data.totalPages === 'number' ? data.totalPages : null;
                    const serverPage = typeof data.page === 'number' ? data.page : null;
                    const serverPageSize = typeof data.pageSize === 'number' ? data.pageSize : null;
                    return {
                        items,
                        totalCount,
                        totalPages,
                        page: serverPage,
                        pageSize: serverPageSize,
                        filters: {
                            month: Array.isArray(filtersFromApi.months) ? filtersFromApi.months : [],
                            unit: Array.isArray(filtersFromApi.units) ? filtersFromApi.units : [],
                            connection: Array.isArray(filtersFromApi.connections) ? filtersFromApi.connections : [],
                            license: Array.isArray(filtersFromApi.licenses) ? filtersFromApi.licenses : [],
                            jobType: Array.isArray(filtersFromApi.jobTypes) ? filtersFromApi.jobTypes : [],
                        },
                        totalJobs: typeof data.totalJobs === 'number' ? data.totalJobs : null,
                        totalSizeMb: typeof data.totalSizeMb === 'number' ? data.totalSizeMb : null,
                        totalSizeGb: typeof data.totalSizeGb === 'number' ? data.totalSizeGb : null,
                    };
                },
                enabled: userLoaded && !!companyId,
                keepPreviousData: true,
            };
        }),
    );

    const detailedDataByCompany = useMemo(() => {
        const map = {};
        expandedCompanyIds.forEach((id, i) => {
            const result = detailedQueries[i];
            const items = result?.data?.items ?? [];
            const totalCount = result?.data?.totalCount ?? 0;
            const filters = result?.data?.filters ?? null;
            const totalJobs = result?.data?.totalJobs ?? null;
            const totalSizeMb = result?.data?.totalSizeMb ?? null;
            const totalSizeGb = result?.data?.totalSizeGb ?? null;
            const totalPages = result?.data?.totalPages ?? null;
            const page = result?.data?.page ?? null;
            const pageSize = result?.data?.pageSize ?? null;
            map[id] = { items, totalCount, filters, totalJobs, totalSizeMb, totalSizeGb, totalPages, page, pageSize };
        });
        return map;
    }, [expandedCompanyIds, detailedQueries]);

    const loadingByCompany = useMemo(() => {
        const map = {};
        expandedCompanyIds.forEach((id, i) => {
            const result = detailedQueries[i];
            const hasData = (result?.data?.items?.length ?? 0) > 0;
            map[id] = (result?.isLoading ?? false) && !hasData;
        });
        return map;
    }, [expandedCompanyIds, detailedQueries]);

    const fetchingByCompany = useMemo(() => {
        const map = {};
        expandedCompanyIds.forEach((id, i) => {
            map[id] = detailedQueries[i]?.isFetching ?? false;
        });
        return map;
    }, [expandedCompanyIds, detailedQueries]);

    const companiesListRequest = useQuery(
        ['auth-companies'],
        async () => {
            const res = await axios.get(`${apiAuthBaseUrl}/companies`);
            return Array.isArray(res.data) ? res.data : [];
        },
        {
            enabled: userLoaded && activeTab === 'Settings',
            refetchOnWindowFocus: false,
        },
    );

    const dataimportRequest = useQuery(
        ['system-dataimport', currentCompany],
        async () => {
            const config = !currentUser.isSuperuser() ? {} : {
                headers: {
                    'CompanyId': currentCompany?.id,
                },
            };
            const res = await axios.get(`${apiBaseUrl}/System/dataimport`, config);
            return res.data;
        },
        {
            enabled: userLoaded && activeTab === 'Settings',
            refetchOnWindowFocus: false,
        },
    );

    const putDataimportMutation = useMutation(
        (payload) => axios.put(`${apiBaseUrl}/System/dataimport`, payload, {
            headers: currentUser.isSuperuser() && currentCompany?.id ? { CompanyId: currentCompany.id } : {},
        }),
        {
            onSuccess: () => {
                queueToggleInProgressRef.current = false;
                togglingCompanyIdRef.current = null;
                queryClient.invalidateQueries(['system-dataimport']);
                queryClient.invalidateQueries(['worker-import-stats']);
                queryClient.invalidateQueries(['worker-import-stats-by-interval-chart']);
            },
            onError: (err) => {
                queueToggleInProgressRef.current = false;
                togglingCompanyIdRef.current = null;
                const msg = err?.response?.data?.message || err?.message || 'Error saving';
                AlertToastr.showErrorAlert(msg);
            },
        }
    );

    useEffect(() => {
        if (listRequest.isSuccess) {
            const listData = listRequest.data.status === 200 ? listRequest.data.data : [];
            setData(listData);
        }
    }, [listRequest.isSuccess, listRequest.data]);

    const companiesList = useMemo(() => {
        const list = companiesListRequest.data;
        return Array.isArray(list) ? list : [];
    }, [companiesListRequest.data]);

    const dataimportMap = useMemo(() => {
        const raw = dataimportRequest.data;
        return raw && typeof raw === 'object' ? raw : {};
    }, [dataimportRequest.data]);

    const companiesFilteredList = useMemo(() => {
        if (!settingsSearchTerm || !settingsSearchTerm.trim()) return companiesList;
        const term = settingsSearchTerm.trim().toLowerCase();
        return companiesList.filter(
            (c) => (c.name || '').toLowerCase().includes(term)
        );
    }, [companiesList, settingsSearchTerm]);

    const cacheStatsRequest = useQuery(
        ['system-cache-stats', currentCompany?.id],
        async () => {
            const config = !currentUser.isSuperuser() ? {} : {
                headers: { CompanyId: currentCompany?.id },
            };
            const res = await axios.get(`${apiBaseUrl}/System/cache/stats`, config);
            return res.data;
        },
        {
            enabled: activeTab === 'Settings' && companiesList.length > 0,
            refetchOnWindowFocus: false,
            // Follow a jobs cache build until it is ready
            refetchInterval: (data) => (
                Object.values(data?.configurations || {}).some(c => c?.isJobsCacheBuilding) ||
                Object.values(data?.jobsCacheBuilds || {}).some(b => b?.status === 'Building')
                    ? 3000
                    : false
            ),
        },
    );

    const cacheRequestHeaders = currentUser.isSuperuser() && currentCompany?.id ? { CompanyId: currentCompany.id } : {};

    // The API only starts the build (20-30 min) and answers right away; the stats poll follows it
    const putJobsCacheMutation = useMutation(
        ({ companyId, enabled }) => axios.put(`${apiBaseUrl}/System/cache/jobs/${companyId}`, null, {
            params: { enabled },
            headers: cacheRequestHeaders,
        }),
        {
            onMutate: ({ companyId, enabled }) => {
                // Show "building" immediately instead of waiting for the response
                queryClient.setQueryData(['system-cache-stats', currentCompany?.id], (data) => {
                    if (!data) return data;
                    const configurations = data.configurations || {};
                    return {
                        ...data,
                        configurations: {
                            ...configurations,
                            [companyId]: {
                                ...configurations[companyId],
                                useJobsCache: false,
                                isJobsCacheBuilding: enabled,
                            },
                        },
                    };
                });
            },
            onSuccess: (res) => {
                queryClient.setQueryData(['system-cache-stats', currentCompany?.id], res.data);
            },
            onError: (err) => {
                const msg = err?.response?.data?.message || err?.message || 'Error saving';
                AlertToastr.showErrorAlert(msg);
            },
            onSettled: () => {
                queryClient.invalidateQueries(['system-cache-stats']);
            },
        },
    );

    const cacheStatsByCompanyId = useMemo(() => {
        const map = {};
        const data = cacheStatsRequest.data;
        if (!data) return map;
        const configurations = data.configurations || {};
        const builds = data.jobsCacheBuilds || {};
        companiesList.forEach((company) => {
            const id = company.id;
            map[id] = {
                useCache: !!configurations[id]?.useJobsCache,
                building: !!configurations[id]?.isJobsCacheBuilding,
                // Last build's status and progress; kept for a day after it finishes
                build: builds[id] || null,
            };
        });
        return map;
    }, [cacheStatsRequest.data, companiesList]);

    const settingsDateTypesRequest = useQuery(
        ['worker-import-date-types', 0],
        async () => {
            const res = await axios.get(`${workerBaseUrl}/Import/date-types`, { params: { type: 0 } });
            return Array.isArray(res.data) ? res.data : [];
        },
        {
            enabled: activeTab === 'Settings',
            refetchOnWindowFocus: false,
        },
    );

    const settingsDateTypesOptions = useMemo(
        () => (settingsDateTypesRequest.data ?? []),
        [settingsDateTypesRequest.data],
    );
    const settingsSelectedPeriod = settingsDateType === 'customRange'
        ? { dateType: 'customRange', name: settingsCustomRangeName || 'Custom range' }
        : settingsDateTypesOptions.find((item) => item.dateType === settingsDateType);

    const workerStatsRequest = useQuery(
        ['worker-import-stats', currentCompany?.id, settingsDateType, settingsDateFrom, settingsDateTo],
        async () => {
            const params = {};
            if (settingsDateType !== undefined && settingsDateType !== null && settingsDateType !== 'customRange') {
                params.dateType = settingsDateType;
            }
            if (settingsDateType === 'customRange' && settingsDateFrom && settingsDateTo) {
                params.from = typeof settingsDateFrom === 'string' ? settingsDateFrom : new Date(settingsDateFrom).toISOString();
                params.to = typeof settingsDateTo === 'string' ? settingsDateTo : new Date(settingsDateTo).toISOString();
            }
            const res = await axios.get(`${workerBaseUrl}/Import/stats`, { params });
            return res.data;
        },
        {
            enabled: Boolean(activeTab === 'Settings' && (settingsDateType !== 'customRange' || (!!settingsDateFrom && !!settingsDateTo))),
            refetchOnWindowFocus: false,
        },
    );

    const workerStatsByIntervalChartRequest = useQuery(
        ['worker-import-stats-by-interval-chart', currentCompany?.id, settingsDateType, settingsDateFrom, settingsDateTo, chartAverageRequestSizeUnit],
        async () => {
            const params = {};
            if (settingsDateType === 'customRange' && settingsDateFrom && settingsDateTo) {
                params.from = typeof settingsDateFrom === 'string' ? settingsDateFrom : new Date(settingsDateFrom).toISOString();
                params.to = typeof settingsDateTo === 'string' ? settingsDateTo : new Date(settingsDateTo).toISOString();
            } else if (settingsDateType !== undefined && settingsDateType !== null && settingsDateType !== 'customRange') {
                params.dateType = settingsDateType;
            }
            params.averageRequestSizeUnit = chartAverageRequestSizeUnit;
            const res = await axios.get(`${workerBaseUrl}/Import/stats/by-interval`, { params });
            return res.data;
        },
        {
            enabled: Boolean(
                activeTab === 'Settings' &&
                    (settingsDateType === 'customRange' ? !!settingsDateFrom && !!settingsDateTo : settingsDateType !== undefined && settingsDateType !== null && settingsDateType !== ''),
            ),
            refetchOnWindowFocus: false,
        },
    );

    const workerStatsByCompanyId = useMemo(() => {
        const map = {};
        const raw = workerStatsRequest.data;
        if (!raw?.companies) return map;
        companiesFilteredList.forEach((company) => {
            const companyData = raw.companies[company.id];
            map[company.id] = {
                messages: companyData?.requestAmount ?? '-',
                totalSizeMb: companyData?.sumRequestSizeMb ?? '-',
                totalSizeGb: companyData?.sumRequestSizeGb ?? '-',
            };
        });
        return map;
    }, [workerStatsRequest.data, companiesFilteredList]);

    const normalizeByIntervalSeries = useCallback((raw, seriesKey) => {
        if (!raw?.timeLabels?.length || !raw[seriesKey] || typeof raw[seriesKey] !== 'object') {
            return { series: [], dateOrigin: null };
        }
        const dateOriginMs = raw.from ? new Date(raw.from).getTime() : new Date(raw.timeLabels[0]).getTime();
        const dateOrigin = new Date(dateOriginMs);
        const companyNameById = {};
        companiesFilteredList.forEach((c) => {
            companyNameById[c.id] = c.name ?? c.companyName ?? c.id;
        });
        const seriesList = [];
        const seriesData = raw[seriesKey];
        Object.keys(seriesData).forEach((companyId) => {
            const values = seriesData[companyId];
            if (!Array.isArray(values) || values.length !== raw.timeLabels.length) return;
            const points = raw.timeLabels.map((label, i) => ({
                x: new Date(label).getTime() - dateOriginMs,
                y: Number(values[i]) || 0,
            }));
            seriesList.push({
                name: companyNameById[companyId] ?? companyId,
                points,
            });
        });
        return { series: seriesList, dateOrigin };
    }, [companiesFilteredList]);

    const chartByIntervalData = useMemo(() => {
        const raw = workerStatsByIntervalChartRequest.data;
        if (!raw) return { series: [], dateOrigin: null };
        const seriesKey = raw.requestCountSeries ? 'requestCountSeries' : 'series';
        return normalizeByIntervalSeries(raw, seriesKey);
    }, [workerStatsByIntervalChartRequest.data, normalizeByIntervalSeries]);

    const chartByIntervalDataRight = useMemo(() => {
        const raw = workerStatsByIntervalChartRequest.data;
        if (!raw) return { series: [], dateOrigin: null };
        return normalizeByIntervalSeries(raw, 'averageRequestSizeSeries');
    }, [workerStatsByIntervalChartRequest.data, normalizeByIntervalSeries]);

    const chartDateOrigin = useMemo(() => {
        if (chartByIntervalData.dateOrigin) return chartByIntervalData.dateOrigin;
        if (chartByIntervalDataRight.dateOrigin) return chartByIntervalDataRight.dateOrigin;
        return null;
    }, [chartByIntervalData.dateOrigin, chartByIntervalDataRight.dateOrigin]);

    const chartTimeRange = useMemo(() => {
        const raw = workerStatsByIntervalChartRequest.data;
        if (!raw?.timeLabels?.length) return null;
        const first = new Date(raw.timeLabels[0]).getTime();
        const last = new Date(raw.timeLabels[raw.timeLabels.length - 1]).getTime();
        return { min: first, max: last };
    }, [workerStatsByIntervalChartRequest.data]);

    useEffect(() => {
        try {
            const payload = {
                period: settingsDateType,
                autoRefreshInterval: settingsAutoRefreshInterval,
                autoRefreshStartTime: settingsAutoRefreshStartTime,
            };
            if (settingsDateType === 'customRange' && settingsDateFrom && settingsDateTo) {
                payload.dateFrom = typeof settingsDateFrom === 'string' ? settingsDateFrom : new Date(settingsDateFrom).toISOString();
                payload.dateTo = typeof settingsDateTo === 'string' ? settingsDateTo : new Date(settingsDateTo).toISOString();
                if (settingsCustomRangeName) payload.customRangeName = settingsCustomRangeName;
            }
            localStorage.setItem(DATAUSAGE_SETTINGS_STORAGE_KEY, JSON.stringify(payload));
        } catch (_) {}
    }, [settingsDateType, settingsDateFrom, settingsDateTo, settingsCustomRangeName, settingsAutoRefreshInterval, settingsAutoRefreshStartTime]);

    const autoRefreshIntervalIdRef = useRef(null);
    const statsQueryEnabled = activeTab === 'Settings' && (settingsDateType !== 'customRange' || (!!settingsDateFrom && !!settingsDateTo));
    useEffect(() => {
        if (!statsQueryEnabled) return;
        const option = AUTO_REFRESH_OPTIONS.find((o) => o.value === settingsAutoRefreshInterval && o.ms);
        if (!option?.ms || !workerStatsRequest.refetch) return;
        const intervalMs = option.ms;
        const refetchStats = () => {
            workerStatsRequest.refetch();
            workerStatsByIntervalChartRequest.refetch();
        };
        const startTime = settingsAutoRefreshStartTime ? settingsAutoRefreshStartTime : Date.now();
        const now = Date.now();
        const elapsed = now - startTime;
        const n = Math.max(0, Math.ceil(elapsed / intervalMs));
        let nextRun = startTime + n * intervalMs;
        if (nextRun <= now) {
            nextRun = now + intervalMs;
            refetchStats();
        }
        const delay = Math.max(0, nextRun - now);
        const timeoutId = setTimeout(() => {
            refetchStats();
            autoRefreshIntervalIdRef.current = setInterval(() => refetchStats(), intervalMs);
        }, delay);
        return () => {
            clearTimeout(timeoutId);
            if (autoRefreshIntervalIdRef.current) {
                clearInterval(autoRefreshIntervalIdRef.current);
                autoRefreshIntervalIdRef.current = null;
            }
        };
    }, [statsQueryEnabled, settingsAutoRefreshInterval, settingsAutoRefreshStartTime, workerStatsRequest, workerStatsByIntervalChartRequest]);

    const handleSettingsTableSort = useCallback((columnKey) => {
        setSettingsTableSortBy((prev) => {
            if (prev === columnKey) {
                setSettingsTableSortOrder((order) => {
                    if (order === 'asc') return 'desc';
                    if (order === 'desc') return null;
                    return 'asc';
                });
                return columnKey;
            }
            setSettingsTableSortOrder('asc');
            return columnKey;
        });
    }, []);

    const settingsTableSortedList = useMemo(() => {
        if (!settingsTableSortBy || settingsTableSortOrder == null) return companiesFilteredList;
        const order = settingsTableSortOrder === 'asc' ? 1 : -1;
        const getValue = (company) => {
            const name = (company.name ?? company.companyName ?? '').toLowerCase();
            const useCache = cacheStatsByCompanyId[company.id]?.useCache ?? false;
            const useQueue = !!dataimportMap[company.id]?.useQueue;
            const useMemoryStream = !!dataimportMap[company.id]?.useMemoryStream;
            const stats = workerStatsByCompanyId[company.id];
            const messages = stats?.messages;
            const mb = stats?.totalSizeMb;
            const gb = stats?.totalSizeGb;
            const num = (v) => (v === '-' || v === undefined || v === null ? -Infinity : Number(v));
            switch (settingsTableSortBy) {
                case 'companyName': return name;
                case 'cache': return useCache ? 1 : 0;
                case 'queue': return useQueue ? 1 : 0;
                case 'importCache': return useMemoryStream ? 1 : 0;
                case 'messages': return num(messages);
                case 'totalSizeMb': return num(mb);
                case 'totalSizeGb': return num(gb);
                default: return null;
            }
        };
        return [...companiesFilteredList].sort((a, b) => {
            const va = getValue(a);
            const vb = getValue(b);
            if (typeof va === 'string' && typeof vb === 'string') {
                return order * (va.localeCompare(vb));
            }
            if (va < vb) return -order;
            if (va > vb) return order;
            return 0;
        });
    }, [companiesFilteredList, settingsTableSortBy, settingsTableSortOrder, workerStatsByCompanyId, dataimportMap, cacheStatsByCompanyId]);

    const showSortArrow = (columnKey) => settingsTableSortBy === columnKey && settingsTableSortOrder != null;

    useEffect(() => {
        if (companiesList.length > 0 && !selectedCompanyId) {
            setSelectedCompanyId(companiesList[0].id);
        }
    }, [companiesList, selectedCompanyId]);

    // The API replaces the company's whole entry: send every flag, only the toggled one changed
    const handleDataImportToggle = useCallback((companyId, name, changes) => {
        if (queueToggleInProgressRef.current || putDataimportMutation.isLoading) return;
        queueToggleInProgressRef.current = true;
        togglingCompanyIdRef.current = companyId;
        const current = dataimportMap[companyId] || {};
        const payload = {
            [companyId]: {
                companyName: name,
                useQueue: !!current.useQueue,
                useMemoryStream: !!current.useMemoryStream,
                ...changes,
            },
        };
        putDataimportMutation.mutate(payload);
    }, [putDataimportMutation, dataimportMap]);

    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
    };

    const handleDataUsageRowClick = useCallback((row) => {
        const companyId = row.companyId ?? row.id;
        setExpandedCompanyIds((prev) =>
            prev.includes(companyId)
                ? prev.filter((id) => id !== companyId)
                : [...prev, companyId],
        );
        setDetailedFiltersByCompany((prev) => {
            if (prev[companyId]) return prev;
            return { ...prev, [companyId]: { ...defaultDetailedFilters } };
        });
        setDetailedPaginationByCompany((prev) => {
            if (prev[companyId]) return prev;
            return { ...prev, [companyId]: getDefaultPagination() };
        });
    }, [defaultDetailedFilters, getDefaultPagination]);

    useEffect(() => {
        setDetailedFiltersByCompany((prev) => {
            let changed = false;
            const next = { ...prev };
            expandedCompanyIds.forEach((id) => {
                if (!next[id]) {
                    next[id] = { ...defaultDetailedFilters };
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
        setDetailedPaginationByCompany((prev) => {
            let changed = false;
            const next = { ...prev };
            expandedCompanyIds.forEach((id) => {
                if (!next[id]) {
                    next[id] = getDefaultPagination();
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [expandedCompanyIds, defaultDetailedFilters, getDefaultPagination]);

    const getDisplayValue = useCallback((item, key) => {
        const v = {
            month: item.month ?? '-',
            unit: item.unit ?? item.unitName ?? item.unitUId ?? '-',
            connection: item.connection ?? '-',
            license: item.license ?? item.licenseName ?? item.licenseId ?? '-',
            jobType: item.jobType ?? '-',
            jobCount: item.jobCount ?? '-',
            totalSize: item.totalSizeMb ?? item.totalSizeKb ?? '-',
        }[key];
        return (v ?? '-').toString();
    }, []);

    const setDetailedFilterForCompany = useCallback((companyId, key, value) => {
        setDetailedFiltersByCompany((prev) => ({
            ...prev,
            [companyId]: { ...(prev[companyId] || defaultDetailedFilters), [key]: value },
        }));
        setDetailedPaginationByCompany((prev) => ({
            ...prev,
            [companyId]: { ...(prev[companyId] || getDefaultPagination()), page: 0 },
        }));
    }, [defaultDetailedFilters, getDefaultPagination]);

    const setDetailedPaginationForCompany = useCallback((companyId, updater) => {
        setDetailedPaginationByCompany((prev) => ({
            ...prev,
            [companyId]: updater(prev[companyId] || getDefaultPagination()),
        }));
    }, [getDefaultPagination]);

    const getExpandedRowContent = useCallback(
        (companyId) => {
            const {
                items = [],
                totalCount = 0,
                filters: serverFilterOptions = null,
                totalJobs: serverTotalJobs = null,
                totalSizeMb: serverTotalSizeMb = null,
                totalSizeGb: serverTotalSizeGb = null,
                totalPages: serverTotalPages = null,
                page: serverPage = null,
                pageSize: serverPageSize = null,
            } = detailedDataByCompany[companyId] ?? {};
            const pagination = detailedPaginationByCompany[companyId] || getDefaultPagination();
            return (
                <DetailedTableContent
                    detailedList={items}
                    totalCount={totalCount}
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    onPageChange={(newPage) => setDetailedPaginationForCompany(companyId, (p) => ({ ...p, page: newPage }))}
                    onPageSizeChange={(newPageSize) => setDetailedPaginationForCompany(companyId, (p) => ({ ...p, pageSize: newPageSize, page: 0 }))}
                    detailedFilters={detailedFiltersByCompany[companyId] ?? defaultDetailedFilters}
                    setDetailedFilter={(key, value) => setDetailedFilterForCompany(companyId, key, value)}
                    getDisplayValue={getDisplayValue}
                    serverFilterOptions={serverFilterOptions}
                    totalJobs={serverTotalJobs}
                    totalSizeMb={serverTotalSizeMb}
                    totalSizeGb={serverTotalSizeGb}
                    serverTotalPages={serverTotalPages}
                    serverPage={serverPage}
                    serverPageSize={serverPageSize}
                    isFetching={fetchingByCompany[companyId]}
                />
            );
        },
        [detailedDataByCompany, detailedFiltersByCompany, detailedPaginationByCompany, defaultDetailedFilters, getDefaultPagination, setDetailedFilterForCompany, setDetailedPaginationForCompany, getDisplayValue, fetchingByCompany],
    );

    const getExpandedRowLoading = useCallback(
        (companyId) => loadingByCompany[companyId] ?? false,
        [loadingByCompany],
    );

    return (
        <>
            <style>{`
                .dataUsageDetailedFilterListbox {
                    padding-left: 0 !important;
                    max-height: 280px !important;
                    overflow-y: auto !important;
                }
                .dataUsageDetailedFilterListbox .MuiAutocomplete-option {
                    padding-left: 0 !important;
                }
                .dataUsageDetailedTable .MuiAutocomplete-root .MuiOutlinedInput-root {
                    display: flex;
                    align-items: center;
                }
                .tableHolder tr.dataUsageExpandedRow:hover {
                    background-color: transparent !important;
                }
                .tableHolder tr.dataUsageExpandedRow td {
                    cursor: default !important;
                }
                .tableHolder .MuiIconButton-root + .MuiIconButton-root {
                    margin-left: 0px !important;
                    margin-right: 10px !important;
                }    
                .dataUsageDetailedTable thead tr:hover {
                    background-color: transparent !important;
                }
                .dataUsageDetailedTable thead tr td,
                .dataUsageDetailedTable thead tr th {
                    cursor: default !important;
                }
                .dataUsageDetailedTable tbody tr:hover {
                    background-color: rgba(68, 68, 68, 0.03);
                }
                .dataUsageDetailedTableInner {
                    animation: dataUsageFadeIn 0.35s ease-out;
                }
                @keyframes dataUsageFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .dataUsageChartsRow {
                    margin-top: 5px;
                    display: grid;
                    grid-template-columns: 50fr 50fr;
                    gap: 15px;
                    align-items: stretch;
                }
                @media (max-width: 992px) {
                    .dataUsageChartsRow {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
            <div className="mainContainer">
            {(showDataUsageTab || showSettingsTab || showFleetViewSettingsTab) && (
                <div className="pageBtnSection">
                    <div className="part">
                        <ul className="tabsLike mb0">
                            {showDataUsageTab && (
                                <li
                                    className={`tabsLikeItem ${activeTab === 'Companies' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('Companies')}
                                >
                                    Companies
                                </li>
                            )}
                            {showSettingsTab && (
                                <li
                                    className={`tabsLikeItem ${activeTab === 'Settings' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('Settings')}
                                >
                                    Settings
                                </li>
                            )}
                            <li
                                className={`tabsLikeItem ${activeTab === 'Unit Settings Log' ? 'active' : ''}`}
                                onClick={() => handleTabChange('Unit Settings Log')}
                            >
                                {translation.dataUsageUnitSettingsLogTabLabel}
                            </li>
                            {showFleetViewSettingsTab ? (
                                <li
                                    className={`tabsLikeItem ${activeTab === 'Fleet View Settings' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('Fleet View Settings')}
                                >
                                    {translation.dataUsageFleetViewSettingsTabLabel}
                                </li>
                            ) : null}
                        </ul>
                    </div>
                </div>
            )}

            {showDataUsageTab && activeTab === 'Companies' && (
                <div className="pageCard">
                    <ListTable
                        emptyDataText={
                            <span>
                                <span>No data found.</span>
                            </span>
                        }
                        request={listRequest}
                        columns={tableColumns}
                        data={data}
                        onRowClick={handleDataUsageRowClick}
                        getRowId={(row) => row.original.companyId ?? row.original.id}
                        expandedRowIds={expandedCompanyIds}
                        getExpandedRowContent={getExpandedRowContent}
                        getExpandedRowLoading={getExpandedRowLoading}
                        expandedRowClassName="dataUsageExpandedRow"
                        enablePagination={false}
                    />
                </div>
            )}

            {showSettingsTab && activeTab === 'Settings' && (
                <>
                    <div className="pageCard">
                        <div className="pageBtnSection pt0 mt5">
                            <div className="searchHolder" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                                <div className="inputHolder" style={{ display: 'flex', verticalAlign: 'center', flex: '1 1 auto', minWidth: 200 }}>
                                    <SearchInput
                                        search={settingsSearchTerm}
                                        onChange={setSettingsSearchTerm}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                    <span style={{ fontSize: 13, color: isDarkTheme ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.6)' }}>Refresh dashboard</span>
                                    <IconButton
                                        type="button"
                                        size="small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            workerStatsRequest.refetch();
                                            workerStatsByIntervalChartRequest.refetch();
                                        }}
                                        title="Refresh"
                                        disabled={workerStatsRequest.isLoading || workerStatsByIntervalChartRequest.isLoading}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                        variant="outlined"
                                        margin="none"
                                        size="small"
                                        onClick={(e) => setSettingsAutoRefreshSelectAnchor(e.currentTarget)}
                                        value={AUTO_REFRESH_OPTIONS.find((o) => o.value === settingsAutoRefreshInterval)?.label ?? 'Off'}
                                        style={{ minWidth: 72, maxWidth: 100 }}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: <ArrowDropDownIcon style={{ color: settingsSelectArrowColor, cursor: 'pointer' }} />,
                                        }}
                                    />
                                    <Popover
                                        open={Boolean(settingsAutoRefreshSelectAnchor)}
                                        anchorEl={settingsAutoRefreshSelectAnchor}
                                        onClose={() => setSettingsAutoRefreshSelectAnchor(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    >
                                        <div style={{ minWidth: 100 }}>
                                            {AUTO_REFRESH_OPTIONS.map((opt) => (
                                                <MenuItem
                                                    key={opt.value}
                                                    onClick={() => {
                                                        setSettingsAutoRefreshInterval(opt.value);
                                                        if (opt.value !== 'off') setSettingsAutoRefreshStartTime(Date.now());
                                                        setSettingsAutoRefreshSelectAnchor(null);
                                                    }}
                                                >
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </div>
                                    </Popover>
                                    <span style={{ fontSize: 13, color: isDarkTheme ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.6)', marginLeft: 15 }}>Period</span>
                                    <TextField
                                        disabled={settingsDateTypesRequest.isLoading}
                                        variant="outlined"
                                        margin="none"
                                        size="small"
                                        onClick={(e) => setSettingsPeriodPopoverAnchor(e.currentTarget)}
                                        value={settingsSelectedPeriod?.name ?? ''}
                                        placeholder="Select period"
                                        style={{ minWidth: 180 }}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: <ArrowDropDownIcon style={{ color: settingsSelectArrowColor, cursor: 'pointer' }} />,
                                        }}
                                    />
                                    <Popover
                                        open={Boolean(settingsPeriodPopoverAnchor)}
                                        anchorEl={settingsPeriodPopoverAnchor}
                                        onClose={() => setSettingsPeriodPopoverAnchor(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    >
                                        <div style={{ minWidth: 180, maxHeight: 320, overflowY: 'auto' }}>
                                            {settingsDateTypesOptions
                                                .filter((item) => item.dateType !== 'customRange' && (item.name || '').toLowerCase() !== 'custom range')
                                                .map((item) => (
                                                    <MenuItem
                                                        key={item.dateType}
                                                        value={item.dateType}
                                                        onClick={() => {
                                                            setSettingsDateType(item.dateType);
                                                            setSettingsDateFrom(null);
                                                            setSettingsDateTo(null);
                                                            setSettingsCustomRangeName(null);
                                                            setSettingsPeriodPopoverAnchor(null);
                                                        }}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            <SettingsDateRangePicker
                                                dateFrom={settingsDateFrom}
                                                dateTo={settingsDateTo}
                                                setDateFrom={setSettingsDateFrom}
                                                setDateTo={setSettingsDateTo}
                                                onChange={(payload) => {
                                                    setSettingsDateType('customRange');
                                                    if (payload.dateFrom) setSettingsDateFrom(payload.dateFrom instanceof Date ? payload.dateFrom.toISOString() : payload.dateFrom);
                                                    if (payload.dateTo) setSettingsDateTo(payload.dateTo instanceof Date ? payload.dateTo.toISOString() : payload.dateTo);
                                                    if (payload.name) setSettingsCustomRangeName(payload.name);
                                                    setSettingsPeriodPopoverAnchor(null);
                                                }}
                                                onReset={() => setSettingsCustomRangeName(null)}
                                                handleClosePopover={() => setSettingsPeriodPopoverAnchor(null)}
                                                handleReset={() => {
                                                    setSettingsDateType(settingsDateTypesOptions[0]?.dateType);
                                                    setSettingsDateFrom(null);
                                                    setSettingsDateTo(null);
                                                    setSettingsCustomRangeName(null);
                                                }}
                                            />
                                        </div>
                                    </Popover>
                                </div>
                            </div>
                        </div>
                        <div className="tableHolder dataUsageSettingsTableHolder">
                            {companiesListRequest.isLoading && (
                                <div style={{ padding: 16, color: 'rgba(68, 68, 68, 0.88)' }}>Loading...</div>
                            )}
                            {!companiesListRequest.isLoading && companiesList.length === 0 && (
                                <div style={{ padding: 16, color: '#757575' }}>No companies</div>
                            )}
                            {!companiesListRequest.isLoading && companiesList.length > 0 && companiesFilteredList.length === 0 && (
                                <div style={{ padding: 16, color: '#757575' }}>No matching companies.</div>
                            )}
                            {!companiesListRequest.isLoading && companiesFilteredList.length > 0 && (
                                <table>
                                    <thead>
                                        <tr className="headingRow">
                                            <th
                                                onClick={() => handleSettingsTableSort('companyName')}
                                                className={showSortArrow('companyName') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                {translation.dataUsageCompanyNameLabel}
                                            </th>
                                            <th
                                                onClick={() => handleSettingsTableSort('cache')}
                                                className={showSortArrow('cache') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                <span>Cache</span>
                                            </th>
                                            <th
                                                onClick={() => handleSettingsTableSort('queue')}
                                                className={showSortArrow('queue') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                <span>Queue</span>
                                            </th>
                                            <th
                                                onClick={() => handleSettingsTableSort('importCache')}
                                                className={showSortArrow('importCache') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                title="Keep the last 15 minutes of imported data in memory"
                                            >
                                                <span>Import cache</span>
                                            </th>
                                            <th
                                                onClick={() => handleSettingsTableSort('messages')}
                                                className={showSortArrow('messages') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                <span>Messages</span>
                                            </th>
                                            <th
                                                onClick={() => handleSettingsTableSort('totalSizeMb')}
                                                className={showSortArrow('totalSizeMb') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                Total size (Mb)
                                            </th>
                                            <th
                                                onClick={() => handleSettingsTableSort('totalSizeGb')}
                                                className={showSortArrow('totalSizeGb') ? (settingsTableSortOrder === 'asc' ? 'hasArrow up' : 'hasArrow') : ''}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                Total size (Gb)
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {settingsTableSortedList.map((company) => {
                                            const useQueue = !!dataimportMap[company.id]?.useQueue;
                                            const useMemoryStream = !!dataimportMap[company.id]?.useMemoryStream;
                                            const stats = workerStatsByCompanyId[company.id];
                                            const isLoading = workerStatsRequest.isLoading;
                                            const isCacheLoading = cacheStatsRequest.isLoading;
                                            const companyName = company.name ?? company.companyName ?? '';
                                            return (
                                        <tr
                                            key={company.id}
                                            onClick={() => setSelectedCompanyId(company.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                <button
                                                    type="button"
                                                    className="companyNameLink"
                                                    onClick={(e) => handleCompanyNameClick(e, company.id)}
                                                >
                                                    {companyName}
                                                </button>
                                            </td>
                                            <td className="text-center switchCell">
                                                {isCacheLoading ? '...' : (() => {
                                                    const cacheStats = cacheStatsByCompanyId[company.id];
                                                    const build = cacheStats?.build;
                                                    const building = !!cacheStats?.building;
                                                    const progress = build && build.totalJobs != null
                                                        ? ` ${build.processedJobs} / ${build.totalJobs} jobs`
                                                        : '';
                                                    const openLog = (e) => {
                                                        e.stopPropagation();
                                                        setCacheLogCompany({ id: company.id, name: companyName });
                                                    };
                                                    return (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                            <Switch
                                                                color="primary"
                                                                checked={!!(cacheStats?.useCache || building)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    putJobsCacheMutation.mutate({ companyId: company.id, enabled: e.target.checked });
                                                                }}
                                                            />
                                                            {building && (
                                                                <IconButton size="small" onClick={openLog} title={`Building cache...${progress} (click for log)`}>
                                                                    <CircularProgress size={16} />
                                                                </IconButton>
                                                            )}
                                                            {!building && build?.status === 'Failed' && (
                                                                <IconButton size="small" onClick={openLog} title="Cache build failed (click for log)">
                                                                    <ErrorOutlineIcon fontSize="small" style={{ color: '#d32f2f' }} />
                                                                </IconButton>
                                                            )}
                                                            {!building && build && build.status !== 'Failed' && build.status !== 'Building' && (
                                                                <IconButton size="small" onClick={openLog} title={`Last cache build: ${build.status} (click for log)`}>
                                                                    <HistoryIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="text-center switchCell">
                                                <Switch
                                                    color="primary"
                                                    checked={useQueue}
                                                    disabled={putDataimportMutation.isLoading && togglingCompanyIdRef.current === company.id}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleDataImportToggle(company.id, companyName, { useQueue: e.target.checked });
                                                    }}
                                                />
                                            </td>
                                            <td className="text-center switchCell">
                                                <Switch
                                                    color="primary"
                                                    checked={useMemoryStream}
                                                    disabled={putDataimportMutation.isLoading && togglingCompanyIdRef.current === company.id}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleDataImportToggle(company.id, companyName, { useMemoryStream: e.target.checked });
                                                    }}
                                                />
                                            </td>
                                            <td>{isLoading ? '...' : (stats?.messages ?? '-')}</td>
                                            <td>{isLoading ? '...' : (stats?.totalSizeMb ?? '-')}</td>
                                            <td>{isLoading ? '...' : (stats?.totalSizeGb ?? '-')}</td>
                                        </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    <div className="pageCard" style={{ marginTop: 15 }}>
                        <div className="dataUsageChartsRow">
                            <div style={{ minWidth: 0 }}>
                                <DataUsageStatsChart
                                    series={chartByIntervalData.series}
                                    dateOrigin={chartDateOrigin}
                                    timeRange={chartTimeRange}
                                    loading={workerStatsByIntervalChartRequest.isLoading}
                                    embedded
                                />
                            </div>
                            <div style={{ minWidth: 0, position: 'relative' }}>
                                <div style={{ position: 'absolute', top: 4, right: 24, zIndex: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 13, color: isDarkTheme ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.6)', marginRight: 5 }}>Unit</span>
                                    <Select
                                        value={chartAverageRequestSizeUnit}
                                        onChange={(e) => setChartAverageRequestSizeUnit(Number(e.target.value))}
                                        variant="outlined"
                                        size="small"
                                        style={{ minWidth: 72 }}
                                        IconComponent={(iconProps) => (
                                            <ArrowDropDownIcon {...iconProps} style={{ color: settingsSelectArrowColor }} />
                                        )}
                                    >
                                        <MenuItem value={0}>kb</MenuItem>
                                        <MenuItem value={1}>mb</MenuItem>
                                        <MenuItem value={2}>gb</MenuItem>
                                    </Select>
                                </div>
                                <DataUsageStatsChart
                                    series={
                                        chartByIntervalDataRight.series.length > 0
                                            ? chartByIntervalDataRight.series
                                            : chartByIntervalData.series.map((s) => ({ name: s.name, points: [] }))
                                    }
                                    dateOrigin={chartDateOrigin}
                                    timeRange={chartTimeRange}
                                    loading={workerStatsByIntervalChartRequest.isLoading}
                                    embedded
                                    legendPaddingRight={150}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {(showDataUsageTab || showSettingsTab) && activeTab === 'Unit Settings Log' && (
                <UnitSettingsLog />
            )}

            {showFleetViewSettingsTab && activeTab === 'Fleet View Settings' && (
                <FleetViewSettings />
            )}
        </div>

            <CompanySlidePanel
                open={companyPanelOpen}
                companyId={selectedPanelCompanyId}
                onClose={() => {
                    setCompanyPanelOpen(false);
                    setSelectedPanelCompanyId(null);
                }}
            />
            <JobsCacheBuildLogDialog
                open={!!cacheLogCompany}
                companyId={cacheLogCompany?.id}
                companyName={cacheLogCompany?.name}
                headers={cacheRequestHeaders}
                onClose={() => setCacheLogCompany(null)}
            />
        </>
    );
};

export default DataUsage;
